import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import {
  PROCESSING_LISTINGS_COLLECTION_NAME,
  ProcessedListingData
} from "../../@types/sharedSchema";
import type { AlgoliaHit } from "instantsearch.js";

// Point reads against the canonical Firestore listings collection
// (`yad2-listings-latest`). Use these wherever the caller already knows the
// listing's Firestore doc id (== Algolia objectID) — they replace the older
// `algoliaClient.getListingsByObjectIds(...)` calls so we don't round-trip
// through Algolia for known-id lookups.
//
// Security model: the Firestore rule on this collection grants `get` to
// anyone (auth or not) but denies `list`. Listing IDs are unguessable UUIDs
// so knowing one is itself the authorization grant. Org/ownership scoping
// for SEARCH stays on Algolia (secured key embeds the filter); these
// helpers are only for the by-id path.
//
// Result shape mirrors `AlgoliaHit<ProcessedListingData>` (adds `objectID`
// and `id`) so call sites that previously consumed Algolia hits keep
// working without further changes.

export type FirestoreListingHit = AlgoliaHit<ProcessedListingData>;

const toHit = (
  id: string,
  data: ProcessedListingData
): FirestoreListingHit => ({ ...(data as any), objectID: id, id });

// Small retry helper for the Firestore point reads below. Transient failures
// (network blip, 503 from the Firestore frontend, brief auth refresh) are
// common enough on a flaky network that one failed `get()` shouldn't drop
// the whole listing from the result. We back off exponentially — 100ms,
// 200ms, 400ms — and give up after `attempts` tries.
async function withRetries<T>(
  attempts: number,
  baseDelayMs: number,
  fn: () => Promise<T>,
  onAttemptFail?: (err: unknown, attempt: number) => void
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      onAttemptFail?.(err, attempt);
      if (attempt === attempts - 1) break;
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastErr;
}

const fetchSingleListingSnapshot = (id: string) =>
  withRetries(
    3,
    100,
    () =>
      db
        .collection(PROCESSING_LISTINGS_COLLECTION_NAME)
        .doc(id)
        .withConverter(GenericConverter<ProcessedListingData>())
        .get(),
    (err, attempt) =>
      console.warn(`fetchListings: get(${id}) attempt ${attempt + 1} failed`, err)
  );

/**
 * A short-lived cache of by-id reads, so that opening a listing does not have to
 * cost a network round trip.
 *
 * ## Why this exists
 *
 * `openListingDialog` has to resolve the document before it can render anything
 * but a spinner, and every id-only caller (the dashboard's "נכסים חדשים" widget,
 * the notifications page, `ListingMatchChip`, the chat card) pays that latency
 * at CLICK time. Reported as "the modal opens right away but loads for a few
 * seconds — this should be instant".
 *
 * It is not the payload: these documents are small (measured 2026-10-01 over 40
 * production docs — min 1.5KB, median 2.4KB, max 3.2KB, and `imageUrls` is half
 * of the biggest one). It is the round trip itself, over a connection the
 * dashboard shares with every live listener on the page. So the fix is to take
 * the round trip off the click, not to make it smaller.
 *
 * ## Why a TTL and not a session cache
 *
 * These are market mirror rows — price and status really do change under us, and
 * a realtor quoting a stale price to a buyer is a worse bug than a slow modal.
 * Two minutes is long enough to cover "hover, click, close, click again" and
 * short enough that nothing on screen can be meaningfully out of date.
 *
 * The PROMISE is cached, not the value, so concurrent callers (a prefetch and
 * the click that overtakes it) share one read instead of racing. A read that
 * resolves to `null` is NOT cached — a missing document is usually a transient
 * failure here, and caching it would pin the failure for two minutes.
 */
const LISTING_CACHE_TTL_MS = 2 * 60 * 1000;
/** Bounded so a long session on a big board cannot grow it without limit. */
const LISTING_CACHE_MAX_ENTRIES = 300;

const listingCache = new Map<
  string,
  { at: number; promise: Promise<FirestoreListingHit | null> }
>();

const readListingThroughCache = (id: string): Promise<FirestoreListingHit | null> => {
  const cached = listingCache.get(id);
  if (cached && Date.now() - cached.at < LISTING_CACHE_TTL_MS) return cached.promise;

  const promise = (async (): Promise<FirestoreListingHit | null> => {
    try {
      const snap = await fetchSingleListingSnapshot(id);
      if (!snap.exists) return null;
      return toHit(snap.id, snap.data() as ProcessedListingData);
    } catch (err) {
      console.error(`fetchListingById(${id}) failed after retries`, err);
      return null;
    }
  })();

  // Drop a miss so the next attempt is a real read rather than a cached `null`.
  void promise.then((hit) => {
    if (!hit) listingCache.delete(id);
  });

  if (listingCache.size >= LISTING_CACHE_MAX_ENTRIES) {
    // Map iterates in insertion order, so the first key is the oldest write.
    const oldest = listingCache.keys().next();
    if (!oldest.done) listingCache.delete(oldest.value);
  }
  listingCache.set(id, { at: Date.now(), promise });
  return promise;
};

/**
 * Fetch a single listing by its Firestore doc id. Returns `null` if the doc
 * doesn't exist or the read fails. Served from `listingCache` when it is warm.
 */
export async function fetchListingById(
  id: string | null | undefined
): Promise<FirestoreListingHit | null> {
  if (!id) return null;
  return readListingThroughCache(id);
}

/**
 * Warm the cache for a listing the user is about to ask for — on hover, on focus,
 * or for the handful of rows a surface has just rendered.
 *
 * Deliberately returns nothing and never rejects: a prefetch is a hint, so a
 * caller must never have to handle it failing, and a failed one simply leaves
 * the click to do the read it would have done anyway.
 */
export function prefetchListingById(id: string | null | undefined): void {
  if (!id) return;
  const cached = listingCache.get(id);
  if (cached && Date.now() - cached.at < LISTING_CACHE_TTL_MS) return;
  void readListingThroughCache(id);
}

/** Drop a listing's cached read — call after an edit that rewrites the document. */
export function invalidateCachedListing(id: string | null | undefined): void {
  if (id) listingCache.delete(id);
}

/**
 * Fetch many listings by Firestore doc id in parallel. Each id is fetched
 * independently with its own retry budget so one flaky read doesn't sink the
 * batch — failed ids are silently dropped (same UX as the old Algolia
 * getObjects path). Result order matches the input.
 */
export async function fetchListingsByIds(
  ids: ReadonlyArray<string | null | undefined>
): Promise<FirestoreListingHit[]> {
  const cleanIds = ids.filter((id): id is string => Boolean(id && id.trim().length > 0));
  if (cleanIds.length === 0) return [];
  const snapshots = await Promise.all(
    cleanIds.map((id) =>
      fetchSingleListingSnapshot(id).catch((err) => {
        console.error(`fetchListingsByIds: gave up on ${id} after retries`, err);
        return null;
      })
    )
  );
  const byId = new Map<string, FirestoreListingHit>();
  snapshots.forEach((snap) => {
    if (!snap || !snap.exists) return;
    byId.set(snap.id, toHit(snap.id, snap.data() as ProcessedListingData));
  });
  return cleanIds.map((id) => byId.get(id)).filter((x): x is FirestoreListingHit => Boolean(x));
}
